import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, useLocation } from "react-router-dom";
import { logout, getLoggedInUser } from "../utils/auth";
import { GoDotFill } from "react-icons/go";
import { getAdminDetails } from "../services/profileService";
import { getSubscriptionAlerts } from "../services/notificationService";

const MasterLayout = ({ children }) => {
  let [sidebarActive, seSidebarActive] = useState(false);
  let [mobileMenu, setMobileMenu] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const location = useLocation();

  const IMAGE_BASE_URL = "https://sramidcard.in/api/uploads/admin/";

  // 1. Initialize adminData from localStorage immediately to stop the "refresh" look
  const [adminData, setAdminData] = useState(() => {
    const saved = localStorage.getItem("admin_profile_persist");
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    // 2. Fetch fresh data in the background
    const fetchAdmin = async () => {
      try {
        const response = await getAdminDetails();
        if (response.status === "success") {
          setAdminData(response.data);
          // Save to storage for the next page load
          localStorage.setItem("admin_profile_persist", JSON.stringify(response.data));
        }
      } catch (error) {
        console.error("Profile fetch error:", error);
      }
    };

    const fetchNotificationCounts = async () => {
      try {
        const res = await getSubscriptionAlerts();
        if (res?.status) {
          setUnreadNotifications(res?.data?.unread_notifications_count || 0);
        }
      } catch (error) {
        console.error("Failed background notification update:", error);
      }
    };

    fetchAdmin();
    fetchNotificationCounts();

    const REALTIME_POLL_INTERVAL = 30000;
    const pollId = setInterval(fetchNotificationCounts, REALTIME_POLL_INTERVAL);

    const handleDropdownClick = (event) => {
      event.preventDefault();
      const clickedLink = event.currentTarget;
      const clickedDropdown = clickedLink.closest(".dropdown");
      if (!clickedDropdown) return;
      const isActive = clickedDropdown.classList.contains("open");

      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        dropdown.classList.remove("open");
        const submenu = dropdown.querySelector(".sidebar-submenu");
        if (submenu) submenu.style.maxHeight = "0px";
      });

      if (!isActive) {
        clickedDropdown.classList.add("open");
        const submenu = clickedDropdown.querySelector(".sidebar-submenu");
        if (submenu) submenu.style.maxHeight = `${submenu.scrollHeight}px`;
      }
    };

    const dropdownTriggers = document.querySelectorAll(".sidebar-menu .dropdown > a, .sidebar-menu .dropdown > Link");
    dropdownTriggers.forEach((trigger) => trigger.addEventListener("click", handleDropdownClick));

    const openActiveDropdown = () => {
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        const submenuLinks = dropdown.querySelectorAll(".sidebar-submenu li a");
        submenuLinks.forEach((link) => {
          if (link.getAttribute("href") === location.pathname || link.getAttribute("to") === location.pathname) {
            dropdown.classList.add("open");
            const submenu = dropdown.querySelector(".sidebar-submenu");
            if (submenu) submenu.style.maxHeight = `${submenu.scrollHeight}px`;
          }
        });
      });
    };

    openActiveDropdown();
    return () => {
      dropdownTriggers.forEach((trigger) => trigger.removeEventListener("click", handleDropdownClick));
      clearInterval(pollId);
    };
  }, [location.pathname]);

  let sidebarControl = () => seSidebarActive(!sidebarActive);
  let mobileMenuControl = () => setMobileMenu(!mobileMenu);

  const loggedUser = getLoggedInUser();
  const isOperator = loggedUser?.role?.toLowerCase() === "operator";

  return (
    <section className={mobileMenu ? "overlay active" : "overlay "}>
      <aside className={sidebarActive ? "sidebar active " : mobileMenu ? "sidebar sidebar-open" : "sidebar"}>
        <button onClick={mobileMenuControl} type="button" className="sidebar-close-btn">
          <Icon icon="radix-icons:cross-2" />
        </button>
        <div>
          <Link to={isOperator ? "/operator-files" : "/"} className="sidebar-logo">
            {/* LOGO: Uses cached adminData.logo if available */}
            <img
              src={adminData?.logo ? `${IMAGE_BASE_URL}${adminData.logo}` : "/assets/images/logo.png"}
              alt="site logo"
              className="light-logo"
            />
            <img src="/assets/images/logo-icon.png" alt="site logo" className="logo-icon" />
          </Link>
        </div>

        {isOperator ? (
          <div className="sidebar-menu-area">
            <ul className="sidebar-menu" id="sidebar-menu">
              <li>
                <NavLink
                  to="/operator-files"
                  className={(navData) =>
                    navData.isActive ? "active-page" : ""
                  }>
                  <Icon
                    icon="solar:cloud-upload-outline"
                    className="menu-icon"
                  />
                  <span>Operator Files</span>
                </NavLink>
              </li>
            </ul>
          </div>
        ) : (
         <div className="sidebar-menu-area">
          <ul className="sidebar-menu" id="sidebar-menu">
    
            <li>
              <NavLink
                to="/"
                className={(navData) =>
                  navData.isActive ? "active-page" : ""
                }>
                <Icon
                  icon="solar:home-smile-angle-outline"
                  className="menu-icon"
                />
                <span>Dashboard</span>
              </NavLink>
            </li>
            <li className="dropdown">
              <Link to="#">
                <Icon icon="mingcute:user-3-line" className="menu-icon" />
                <span>User Management</span>
              </Link>

              <ul className="sidebar-submenu">
                <li>
                  <NavLink
                    to="/add-user"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-primary-600" />
                    <span>Add User</span>
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/user-list"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-success-600" />
                    <span>User List</span>
                  </NavLink>
                </li>
              </ul>
            </li>

            <li className="dropdown">
              <Link to="#">
                <Icon icon="mdi:account-group-outline" className="menu-icon" />
                <span>Team Management</span>
              </Link>

              <ul className="sidebar-submenu">
                <li>
                  <NavLink
                    to="/add-assign"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-primary-600" />
                    <span>Assign Sales</span>
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/assign-list"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-info-main" />
                    <span>Assign Sales List</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/teams-list"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-info-main" />
                    <span>Team List</span>
                  </NavLink>
                </li>
              </ul>
            </li>

            <li className="dropdown">
              <Link to="#">
                <Icon icon="mingcute:storage-line" className="menu-icon" />
                <span>Category</span>
              </Link>
              <ul className="sidebar-submenu">
                <li>
                  <NavLink
                    to="/categoryes"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }>
                    <i className="ri-circle-fill circle-icon text-primary-600 w-auto" />{" "}
                    Category
                  </NavLink>
                </li>
              </ul>
            </li>

            <li className="dropdown">
              <Link to="#">
                <Icon icon="mdi:school-outline" className="menu-icon" />
                <span>School Management</span>
              </Link>

              <ul className="sidebar-submenu">
                <li>
                  <NavLink
                    to="/add-school"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-primary-600" />
                    <span>Add School</span>
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/school-list"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-success-600" />
                    <span>School List</span>
                  </NavLink>
                </li>

                {/* ✅ NEW LINK */}
                <li>
                  <NavLink
                    to="/assign-devices"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-primary-600" />
                    <span>Assign Devices</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/school-management"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-warning-600" />
                    <span>File Manager</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/firmware-manage"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-danger-600" />
                    <span>Firmware Manage</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/school-file"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-warning-600" />
                    <span>School file</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/operator-files"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-info-600" />
                    <span>Operator Files</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/student-list"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-success-600" />
                    <span>Student List</span>
                  </NavLink>
                </li>
                <li>
      <NavLink
        to="/manage-package"
        className={({ isActive }) => (isActive ? "active-page" : "")}
      >
        <GoDotFill className="circle-icon text-info-600" />
        <span>Manage Package</span>
      </NavLink>
    </li>

    <li>
      <NavLink
        to="/payment-list"
        className={({ isActive }) => (isActive ? "active-page" : "")}
      >
        <GoDotFill className="circle-icon text-purple-600" />
        <span>Payment List</span>
      </NavLink>
    </li>

    {/* <li>
      <NavLink
        to="/payment-settings"
        className={({ isActive }) => (isActive ? "active-page" : "")}
      >
        <GoDotFill className="circle-icon text-danger-600" />
        <span>Payment Settings</span>
      </NavLink>
    </li> */}
              </ul>
            </li>

            <li>
              <NavLink
                to="/login-image-manage"
                className={({ isActive }) =>
                  isActive ? "active-page" : ""
                }>
                <Icon icon="mdi:image-outline" className="menu-icon" />
                <span>Login Image Manage</span>
              </NavLink>
            </li>

            <li className="dropdown">
              <Link to="#">
                <Icon icon="mdi:clipboard-text-outline" className="menu-icon" />
                <span>Lead Management</span>
              </Link>

              <ul className="sidebar-submenu">
                <li>
                  <NavLink
                    to="/add-lead"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-primary-600" />
                    <span>Add Lead</span>
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/lead-list"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-danger-600" />
                    <span>Lead List</span>
                  </NavLink>
                </li>
                {/* <li>
                  <NavLink
                    to="/lead-status"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-warning-main" />
                    <span>Lead Status</span>
                  </NavLink>
                </li> */}
              </ul>
            </li>

            <li className="dropdown">
              <Link to="#">
                <Icon icon="mdi:bullhorn-outline" className="menu-icon" />
                <span>Campaign Management</span>
              </Link>

              <ul className="sidebar-submenu">
                {/* ADD CAMPAIGN */}
                <li>
                  <NavLink
                    to="/add-campaign"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-primary-600" />
                    <span>Add Campaign</span>
                  </NavLink>
                </li>

                {/* CAMPAIGN LIST / EDIT */}
                <li>
                  <NavLink
                    to="/campaign-list"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-info-main" />
                    <span>Campaign List</span>
                  </NavLink>
                </li>
              </ul>
            </li>

            <li className="dropdown">
              <Link to="#">
                <Icon icon="mdi:package-variant-closed" className="menu-icon" />
                <span>Packages</span>
              </Link>

              <ul className="sidebar-submenu">
                <li>
                  <NavLink
                    to="/add-package"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-primary-600" />
                    <span>Add Package</span>
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/package-list"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-success-600" />
                    <span>Package List</span>
                  </NavLink>
                </li>
              </ul>
            </li>

            <li className="dropdown">
              <Link to="#">
                <Icon icon="mdi:chart-box-outline" className="menu-icon" />
                <span>Reports</span>
              </Link>

              <ul className="sidebar-submenu">
                <li>
                  <NavLink
                    to="/reports-team"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-primary-600" />
                    <span>Team Report</span>
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/call-report"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-success-600" />
                    <span>Call Report</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/user-report"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-success-600" />
                    <span>User Report</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/lead-report"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-success-600" />
                    <span>Lead Report</span>
                  </NavLink>
                </li>
                {/* <li>
                  <NavLink
                    to="/followups-report"
                    className={({ isActive }) =>
                      isActive ? "active-page" : ""
                    }>
                    <GoDotFill className="circle-icon text-success-600" />
                    <span>Followups Report</span>
                  </NavLink>
                </li> */}
              </ul>
            </li>
          </ul>
        </div>
        )}
      </aside>

      <main className={sidebarActive ? "dashboard-main active" : "dashboard-main"}>
        <div className="navbar-header">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-4">
                {/* Desktop Sidebar Toggle Button */}
                <button 
                  type="button" 
                  className="sidebar-toggle d-none d-md-block" 
                  onClick={sidebarControl}
                >
                  <Icon 
                    icon={sidebarActive ? "iconoir:arrow-right" : "heroicons:bars-3-solid"} 
                    className="icon text-2xl non-active" 
                  />
                </button>
                {/* Mobile Menu Button - Shows only on tablet and mobile */}
                <button 
                  type="button" 
                  className="mobile-menu-toggle d-md-none" 
                  onClick={mobileMenuControl}
                >
                  <Icon icon="heroicons:bars-3-solid" className="icon text-2xl" />
                </button>
                <form className="navbar-search">
                  <input type="text" name="search" placeholder="Search" />
                  <Icon icon="ion:search-outline" className="icon" />
                </form>
              </div>
            </div>
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-3">
                <Link 
                  to="/notification" 
                  className="d-flex justify-content-center align-items-center position-relative text-black text-2xl hover-text-primary"
                  style={{ width: "40px", height: "40px" }}
                >
                  <Icon icon="solar:bell-bing-linear" />
                  {unreadNotifications > 0 && (
                    <span 
                      className="position-absolute d-flex align-items-center justify-content-center bg-danger text-white rounded-circle fw-bold" 
                      style={{ 
                        top: "2px", 
                        right: "2px", 
                        fontSize: "10px", 
                        width: "16px", 
                        height: "16px",
                        lineHeight: "1"
                      }}
                    >
                      {unreadNotifications}
                    </span>
                  )}
                </Link>

                <div className="dropdown">
                  <button className="d-flex justify-content-center align-items-center rounded-circle" type="button" data-bs-toggle="dropdown">
                    {/* PROFILE ICON: Uses cached photo if available */}
                    <img
                      src={adminData?.photo ? `${IMAGE_BASE_URL}${adminData.photo}` : "/assets/images/user.png"}
                      alt="user"
                      className="w-40-px h-40-px object-fit-cover rounded-circle"
                      onError={(e) => { e.target.src = "/assets/images/user.png"; }}
                    />
                  </button>
                  <div className="dropdown-menu to-top dropdown-menu-sm">
                    <ul className="to-top-list">
                      {!isOperator && (
                        <li>
                          <Link className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3" to="/view-profile">
                            <Icon icon="solar:user-linear" className="icon text-xl" /> My Profile
                          </Link>
                        </li>
                      )}
                      {isOperator && (
                        <li className="px-3 py-2 border-bottom text-muted small d-flex align-items-center justify-content-between">
                          <span>{loggedUser?.name || loggedUser?.email || "Operator"}</span>
                          <span className="badge bg-primary text-white">Operator</span>
                        </li>
                      )}
                      <li>
                        <Link to="#" className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3" onClick={(e) => { e.preventDefault(); logout(); }}>
                          <Icon icon="lucide:power" className="icon text-xl" /> Log Out
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-main-body">{children}</div>
        <footer className="d-footer">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto"><p className="mb-0">© 2025 Sales Team Management CRM System. All Rights Reserved.</p></div>
            {/* <div className="col-auto"><p className="mb-0">Made by <span className="text-primary-600">My Daddy Online</span></p></div> */}
          </div>
        </footer>
      </main>
    </section>
  );
};

export default MasterLayout;


// import React, { useEffect, useState } from "react";
// import { Icon } from "@iconify/react/dist/iconify.js";
// import { Link, NavLink, useLocation } from "react-router-dom";
// import ThemeToggleButton from "../helper/ThemeToggleButton";
// import { logout, getLoggedInUser } from "../utils/auth";
// import { AddOutlet } from "../pages/AddOutlet";
// import { GiWoodBeam, GiWoodPile } from "react-icons/gi";
// import { GoDotFill } from "react-icons/go";
// import { getAdminDetails } from "../services/profileService"; 
// import { getSubscriptionAlerts } from "../services/notificationService"; // ✅ Imported notification tracking

// const MasterLayout = ({ children }) => {
//   const [sidebarActive, seSidebarActive] = useState(false);
//   const [mobileMenu, setMobileMenu] = useState(false);
//   const [unreadNotifications, setUnreadNotifications] = useState(0); // ✅ Tracks dynamic count
//   const location = useLocation();

//   const IMAGE_BASE_URL = "https://sramidcard.in/api/uploads/admin/";

//   // 1. Initialize adminData from localStorage immediately to stop the "refresh" look
//   const [adminData, setAdminData] = useState(() => {
//     const saved = localStorage.getItem("admin_profile_persist");
//     return saved ? JSON.parse(saved) : null;
//   });

//   useEffect(() => {
//     // 2. Fetch fresh profile details
//     const fetchAdmin = async () => {
//       try {
//         const response = await getAdminDetails();
//         if (response.status === "success") {
//           setAdminData(response.data);
//           localStorage.setItem("admin_profile_persist", JSON.stringify(response.data));
//         }
//       } catch (error) {
//         console.error("Profile fetch error:", error);
//       }
//     };

//     // 3. ✅ Dynamic non-refresh background count fetcher
//     const fetchNotificationCounts = async () => {
//       try {
//         const res = await getSubscriptionAlerts();
//         if (res?.status) {
//           setUnreadNotifications(res?.data?.unread_notifications_count || 0);
//         }
//       } catch (error) {
//         console.error("Failed background notification update:", error);
//       }
//     };

//     // Initial triggers
//     fetchAdmin();
//     fetchNotificationCounts();

//     // ✅ Background auto-polling updates counts seamlessly every 30 seconds
//     const REALTIME_POLL_INTERVAL = 30000;
//     const pollId = setInterval(fetchNotificationCounts, REALTIME_POLL_INTERVAL);

//     // Dropdown UI handlers
//     const handleDropdownClick = (event) => {
//       event.preventDefault();
//       const clickedLink = event.currentTarget;
//       const clickedDropdown = clickedLink.closest(".dropdown");
//       if (!clickedDropdown) return;
//       const isActive = clickedDropdown.classList.contains("open");

//       const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
//       allDropdowns.forEach((dropdown) => {
//         dropdown.classList.remove("open");
//         const submenu = dropdown.querySelector(".sidebar-submenu");
//         if (submenu) submenu.style.maxHeight = "0px";
//       });

//       if (!isActive) {
//         clickedDropdown.classList.add("open");
//         const submenu = clickedDropdown.querySelector(".sidebar-submenu");
//         if (submenu) submenu.style.maxHeight = `${submenu.scrollHeight}px`;
//       }
//     };

//     const dropdownTriggers = document.querySelectorAll(".sidebar-menu .dropdown > a, .sidebar-menu .dropdown > Link");
//     dropdownTriggers.forEach((trigger) => trigger.addEventListener("click", handleDropdownClick));

//     const openActiveDropdown = () => {
//       const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
//       allDropdowns.forEach((dropdown) => {
//         const submenuLinks = dropdown.querySelectorAll(".sidebar-submenu li a");
//         submenuLinks.forEach((link) => {
//           if (link.getAttribute("href") === location.pathname || link.getAttribute("to") === location.pathname) {
//             dropdown.classList.add("open");
//             const submenu = dropdown.querySelector(".sidebar-submenu");
//             if (submenu) submenu.style.maxHeight = `${submenu.scrollHeight}px`;
//           }
//         });
//       });
//     };

//     openActiveDropdown();
    
//     // Clear listeners and recurring poll schedules cleanly on unmount
//     return () => {
//       dropdownTriggers.forEach((trigger) => trigger.removeEventListener("click", handleDropdownClick));
//       clearInterval(pollId);
//     };
//   }, [location.pathname]);

//   let sidebarControl = () => seSidebarActive(!sidebarActive);
//   let mobileMenuControl = () => setMobileMenu(!mobileMenu);

//   return (
//     <section className={mobileMenu ? "overlay active" : "overlay "}>
//       <aside className={sidebarActive ? "sidebar active " : mobileMenu ? "sidebar sidebar-open" : "sidebar"}>
//         <button onClick={mobileMenuControl} type="button" className="sidebar-close-btn">
//           <Icon icon="radix-icons:cross-2" />
//         </button>
//         <div>
//           <Link to="/" className="sidebar-logo">
//             <img
//               src={adminData?.logo ? `${IMAGE_BASE_URL}${adminData.logo}` : "assets/images/logo.png"}
//               alt="site logo"
//               className="light-logo"
//             />
//             <img src="assets/images/logo-icon.png" alt="site logo" className="logo-icon" />
//           </Link>
//         </div>

//         <div className="sidebar-menu-area">
//           <ul className="sidebar-menu" id="sidebar-menu">
//             <li>
//               <NavLink to="/" className={(navData) => navData.isActive ? "active-page" : ""}>
//                 <Icon icon="solar:home-smile-angle-outline" className="menu-icon" />
//                 <span>Dashboard</span>
//               </NavLink>
//             </li>
            
//             {/* User Management */}
//             <li className="dropdown">
//               <Link to="#">
//                 <Icon icon="mingcute:user-3-line" className="menu-icon" />
//                 <span>User Management</span>
//               </Link>
//               <ul className="sidebar-submenu">
//                 <li>
//                   <NavLink to="/add-user" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-primary-600" />
//                     <span>Add User</span>
//                   </NavLink>
//                 </li>
//                 <li>
//                   <NavLink to="/user-list" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-success-600" />
//                     <span>User List</span>
//                   </NavLink>
//                 </li>
//               </ul>
//             </li>

//             {/* Team Management */}
//             <li className="dropdown">
//               <Link to="#">
//                 <Icon icon="mdi:account-group-outline" className="menu-icon" />
//                 <span>Team Management</span>
//               </Link>
//               <ul className="sidebar-submenu">
//                 <li>
//                   <NavLink to="/add-assign" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-primary-600" />
//                     <span>Assign Sales</span>
//                   </NavLink>
//                 </li>
//                 <li>
//                   <NavLink to="/assign-list" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-info-main" />
//                     <span>Assign Sales List</span>
//                   </NavLink>
//                 </li>
//                 <li>
//                   <NavLink to="/teams-list" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-info-main" />
//                     <span>Team List</span>
//                   </NavLink>
//                 </li>
//               </ul>
//             </li>

//             {/* Category */}
//             <li className="dropdown">
//               <Link to="#">
//                 <Icon icon="mingcute:storage-line" className="menu-icon" />
//                 <span>Category</span>
//               </Link>
//               <ul className="sidebar-submenu">
//                 <li>
//                   <NavLink to="/categoryes" className={(navData) => navData.isActive ? "active-page" : ""}>
//                     <i className="ri-circle-fill circle-icon text-primary-600 w-auto" /> Category
//                   </NavLink>
//                 </li>
//               </ul>
//             </li>

//             {/* School Management */}
//             <li className="dropdown">
//               <Link to="#">
//                 <Icon icon="mdi:school-outline" className="menu-icon" />
//                 <span>School Management</span>
//               </Link>
//               <ul className="sidebar-submenu">
//                 <li>
//                   <NavLink to="/add-school" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-primary-600" />
//                     <span>Add School</span>
//                   </NavLink>
//                 </li>
//                 <li>
//                   <NavLink to="/school-list" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-success-600" />
//                     <span>School List</span>
//                   </NavLink>
//                 </li>
//                 <li>
//                   <NavLink to="/school-management" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-warning-600" />
//                     <span>File Manager</span>
//                   </NavLink>
//                 </li>
//                 <li>
//                   <NavLink to="/school-file" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-warning-600" />
//                     <span>School file</span>
//                   </NavLink>
//                 </li>
//                 <li>
//                   <NavLink to="/manage-package" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-info-600" />
//                     <span>Manage Package</span>
//                   </NavLink>
//                 </li>
//                 <li>
//                   <NavLink to="/payment-list" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-purple-600" />
//                     <span>Package Reports</span>
//                   </NavLink>
//                 </li>
//                 <li>
//                   <NavLink to="/payment-settings" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-danger-600" />
//                     <span>Payment Settings</span>
//                   </NavLink>
//                 </li>
//               </ul>
//             </li>

//             {/* Lead Management */}
//             <li className="dropdown">
//               <Link to="#">
//                 <Icon icon="mdi:clipboard-text-outline" className="menu-icon" />
//                 <span>Lead Management</span>
//               </Link>
//               <ul className="sidebar-submenu">
//                 <li>
//                   <NavLink to="/add-lead" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-primary-600" />
//                     <span>Add Lead</span>
//                   </NavLink>
//                 </li>
//                 <li>
//                   <NavLink to="/lead-list" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-danger-600" />
//                     <span>Lead List</span>
//                   </NavLink>
//                 </li>
//               </ul>
//             </li>

//             {/* Campaign Management */}
//             <li className="dropdown">
//               <Link to="#">
//                 <Icon icon="mdi:bullhorn-outline" className="menu-icon" />
//                 <span>Campaign Management</span>
//               </Link>
//               <ul className="sidebar-submenu">
//                 <li>
//                   <NavLink to="/add-campaign" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-primary-600" />
//                     <span>Add Campaign</span>
//                   </NavLink>
//                 </li>
//                 <li>
//                   <NavLink to="/campaign-list" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-info-main" />
//                     <span>Campaign List</span>
//                   </NavLink>
//                 </li>
//               </ul>
//             </li>

//             {/* Packages */}
//             <li className="dropdown">
//               <Link to="#">
//                 <Icon icon="mdi:package-variant-closed" className="menu-icon" />
//                 <span>Packages</span>
//               </Link>
//               <ul className="sidebar-submenu">
//                 <li>
//                   <NavLink to="/add-package" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-primary-600" />
//                     <span>Add Package</span>
//                   </NavLink>
//                 </li>
//                 <li>
//                   <NavLink to="/package-list" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-success-600" />
//                     <span>Package List</span>
//                   </NavLink>
//                 </li>
//               </ul>
//             </li>

//             {/* Reports */}
//             <li className="dropdown">
//               <Link to="#">
//                 <Icon icon="mdi:chart-box-outline" className="menu-icon" />
//                 <span>Reports</span>
//               </Link>
//               <ul className="sidebar-submenu">
//                 <li>
//                   <NavLink to="/reports-team" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-primary-600" />
//                     <span>Team Report</span>
//                   </NavLink>
//                 </li>
//                 <li>
//                   <NavLink to="/call-report" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-success-600" />
//                     <span>Call Report</span>
//                   </NavLink>
//                 </li>
//                 <li>
//                   <NavLink to="/user-report" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-success-600" />
//                     <span>User Report</span>
//                   </NavLink>
//                 </li>
//                 <li>
//                   <NavLink to="/lead-report" className={({ isActive }) => isActive ? "active-page" : ""}>
//                     <GoDotFill className="circle-icon text-success-600" />
//                     <span>Lead Report</span>
//                   </NavLink>
//                 </li>
//               </ul>
//             </li>
//           </ul>
//         </div>
//       </aside>

//       <main className={sidebarActive ? "dashboard-main active" : "dashboard-main"}>
//         <div className="navbar-header">
//           <div className="row align-items-center justify-content-between">
//             <div className="col-auto">
//               <div className="d-flex flex-wrap align-items-center gap-4">
//                 <button type="button" className="sidebar-toggle d-none d-md-block" onClick={sidebarControl}>
//                   <Icon 
//                     icon={sidebarActive ? "iconoir:arrow-right" : "heroicons:bars-3-solid"} 
//                     className="icon text-2xl non-active" 
//                   />
//                 </button>
//                 <button type="button" className="mobile-menu-toggle d-md-none" onClick={mobileMenuControl}>
//                   <Icon icon="heroicons:bars-3-solid" className="icon text-2xl" />
//                 </button>
//                 <form className="navbar-search">
//                   <input type="text" name="search" placeholder="Search" />
//                   <Icon icon="ion:search-outline" className="icon" />
//                 </form>
//               </div>
//             </div>
            
//             <div className="col-auto">
//               <div className="d-flex flex-wrap align-items-center gap-3">
                
//                 {/* ✅ UPDATED PREMIUM NOTIFICATION BELL INTERFACE */}
//                 <Link 
//                   to="/notification" 
//                   className="d-flex justify-content-center align-items-center position-relative text-black text-2xl hover-text-primary"
//                   style={{ width: "40px", height: "40px" }}
//                 >
//                   <Icon icon="solar:bell-bing-linear" />
//                   {/* Dynamic conditional badge rendered only when unread notifications exist */}
//                   {unreadNotifications > 0 && (
//                     <span 
//                       className="position-absolute d-flex align-items-center justify-content-center bg-danger text-white rounded-circle fw-bold" 
//                       style={{ 
//                         top: "2px", 
//                         right: "2px", 
//                         fontSize: "10px", 
//                         width: "16px", 
//                         height: "16px",
//                         lineHeight: "1"
//                       }}
//                     >
//                       {unreadNotifications}
//                     </span>
//                   )}
//                 </Link>

//                 <div className="dropdown">
//                   <button className="d-flex justify-content-center align-items-center rounded-circle" type="button" data-bs-toggle="dropdown">
//                     <img
//                       src={adminData?.photo ? `${IMAGE_BASE_URL}${adminData.photo}` : "assets/images/user.png"}
//                       alt="user"
//                       className="w-40-px h-40-px object-fit-cover rounded-circle"
//                       onError={(e) => { e.target.src = "assets/images/user.png"; }}
//                     />
//                   </button>
//                   <div className="dropdown-menu to-top dropdown-menu-sm">
//                     <ul className="to-top-list">
//                       <li>
//                         <Link className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3" to="/view-profile">
//                           <Icon icon="solar:user-linear" className="icon text-xl" /> My Profile
//                         </Link>
//                       </li>
//                       <li>
//                         <Link to="#" className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3" onClick={(e) => { e.preventDefault(); logout(); }}>
//                           <Icon icon="lucide:power" className="icon text-xl" /> Log Out
//                         </Link>
//                       </li>
//                     </ul>
//                   </div>
//                 </div>

//               </div>
//             </div>
//           </div>
//         </div>

//         <div className="dashboard-main-body">{children}</div>
//         <footer className="d-footer">
//           <div className="row align-items-center justify-content-between">
//             <div className="col-auto"><p className="mb-0">© 2026 Sales Team Management CRM System. All Rights Reserved.</p></div>
//             <div className="col-auto"><p className="mb-0">Made by <span className="text-primary-600">My Daddy Online</span></p></div>
//           </div>
//         </footer>
//       </main>
//     </section>
//   );
// };

// export default MasterLayout;